import { useState, useRef } from 'react'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import { useSizeGuides } from '../../../features/products/hooks/useMetaData'
import { metaDataApi } from '../../../features/products/api/metaDataApi'
import { MetaTable } from '../components/MetaTable'
import { Input } from '../../../shared/components'
import Button from '../../../shared/components/Button'

// TODO: replace with actual DTO fields when available
interface SizeGuideFormData {
  name: string
  description: string
  file: File | null
}

const buildFormData = (data: SizeGuideFormData): FormData => {
  const fd = new FormData()
  // TODO: update sizeGuideData fields to match CreateSizeGuideDto / UpdateSizeGuideDto
  fd.append('sizeGuideData', JSON.stringify({ name: data.name, description: data.description }))
  if (data.file) fd.append('file', data.file)
  return fd
}

export const SizeGuidesTab = () => {
  const { data, isLoading } = useSizeGuides()
  const queryClient = useQueryClient()
  const [editingId, setEditingId] = useState<string | null>(null)

  const createMutation = useMutation({
    mutationFn: (fd: FormData) => metaDataApi.createSizeGuide(fd),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['size-guides'] }),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, fd }: { id: string; fd: FormData }) => metaDataApi.updateSizeGuide(id, fd),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['size-guides'] }); setEditingId(null) },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => metaDataApi.deleteSizeGuide(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['size-guides'] }),
  })

  const items = (data?.result?.data ?? []) as unknown as Record<string, unknown>[]

  const columns = [
    {
      key: 'name',
      label: 'Name',
      render: (item: Record<string, unknown>) => (
        <div className="flex items-center gap-3">
          {item.imageUrl? (
            <img
              src={item.imageUrl as string}
              alt={item.name as string}
              className="w-10 h-10 object-cover rounded-[6px] border border-[#E5E7EB]"
            />
          ) : <></>}
          <span>{item.name as string}</span>
        </div>
      ),
    },
    { key: 'description', label: 'Description' },
  ]

  const renderAddForm = (onDone: () => void) => (
    <SizeGuideForm
      isLoading={createMutation.isPending}
      onSubmit={async (formData) => {
        await createMutation.mutateAsync(buildFormData(formData))
        onDone()
      }}
      onCancel={onDone}
    />
  )

  const renderEditForm = (item: Record<string, unknown>, onDone: () => void) => (
    <SizeGuideForm
      defaultValues={{ name: item.name as string, description: item.description as string, file: null }}
      isLoading={updateMutation.isPending}
      onSubmit={async (formData) => {
        await updateMutation.mutateAsync({ id: item.id as string, fd: buildFormData(formData) })
      }}
      onCancel={onDone}
      isEdit
    />
  )

  return (
    <MetaTable
      title="Size Guides"
      items={items}
      columns={columns}
      isLoading={isLoading}
      addLabel="Add Size Guide"
      renderAddForm={renderAddForm}
      onEdit={(item) => setEditingId(editingId === (item.id as string) ? null : (item.id as string))}
      onDelete={(item) => deleteMutation.mutate(item.id as string)}
      editingId={editingId}
      renderEditForm={renderEditForm}
    />
  )
}

const SizeGuideForm = ({
  defaultValues = { name: '', description: '', file: null },
  isLoading,
  onSubmit,
  onCancel,
  isEdit = false,
}: {
  defaultValues?: SizeGuideFormData
  isLoading: boolean
  onSubmit: (data: SizeGuideFormData) => Promise<void>
  onCancel: () => void
  isEdit?: boolean
}) => {
  const [name, setName] = useState(defaultValues.name)
  const [description, setDescription] = useState(defaultValues.description)
  const [file, setFile] = useState<File | null>(defaultValues.file)
  const [preview, setPreview] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] ?? null
    setFile(selected)
    setPreview(selected ? URL.createObjectURL(selected) : null)
  }

  const isValid = name.trim() && description.trim() && (isEdit || file)

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-4">
        <Input
          label="Name"
          placeholder="e.g. Men's Agbada Guide"
          value={name}
          onChange={e => setName(e.target.value)}
          className="h-[38px]"
        />
        <Input
          label="Description"
          placeholder="Brief description"
          value={description}
          onChange={e => setDescription(e.target.value)}
          className="h-[38px]"
        />
      </div>

      {/* Image upload */}
      <div>
        <label className="block text-[14px] font-[600] text-foreground mb-1">
          Size Guide Image {isEdit && <span className="text-muted-foreground font-[400]">(optional — leave empty to keep existing)</span>}
        </label>
        <div
          onClick={() => fileInputRef.current?.click()}
          className="w-full h-[100px] border border-dashed border-[#B8B5B6] rounded-[10px] bg-muted flex items-center justify-center gap-3 cursor-pointer hover:bg-muted transition-colors"
        >
          {preview ? (
            <img src={preview} alt="preview" className="h-full object-contain rounded-[8px] p-1" />
          ) : (
            <div className="flex flex-col items-center gap-1 text-muted-foreground">
              <svg width="20" height="20" viewBox="0 0 18 18" fill="none">
                <path d="M11.957 11.852L8.994 8.889l-2.963 2.963" stroke="#7C797C" strokeWidth="1.48" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M9 8.889V15.556" stroke="#7C797C" strokeWidth="1.48" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M15.211 13.622a4.444 4.444 0 0 0-1.756-8.511H12.507A7.111 7.111 0 1 0 2.329 12.074" stroke="#7C797C" strokeWidth="1.48" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span className="text-[12px]">Click to upload image</span>
            </div>
          )}
        </div>
        <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
        {file && <p className="text-[12px] text-muted-foreground mt-1">{file.name}</p>}
      </div>

      <div className="flex gap-3">
        <Button size="sm" onClick={() => onSubmit({ name, description, file })} disabled={!isValid || isLoading} loading={isLoading} className="h-[38px]">
          {isEdit ? 'Update' : 'Save'}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel} className="h-[38px]">Cancel</Button>
      </div>
    </div>
  )
}
